import { Auth } from 'aws-amplify';
import { Formik } from 'formik';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { Button, TextInput } from 'src/components';
import AuthContainer from 'src/Containers/AuthContainer';
import { signinValidationSchema } from './validation';
import OnboardingHeaderWrapper from 'src/components/Wrapper/OnboardingHeaderWrapper';
import useLoading from 'src/hooks/useLoading';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/redux/store';
import { getUserProfile } from 'src/redux/profileSlice';

interface SubmitValues {
  email: string;
  password: string;
}

type LocationProps = {
  state: {
    from: Location;
  };
};

const UserLogin = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const navigate = useNavigate();
  const location = useLocation() as unknown as LocationProps;
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (values: SubmitValues) => {
    startLoading();
    const { email, password } = values;
    try {
      const loginUser = await Auth.signIn(email, password);
      const { challengeName } = loginUser || {};
      if (challengeName === 'NEW_PASSWORD_REQUIRED') {
        toast.error(
          'To log in, you need to sign up first and complete your profile. Temporary passwords cannot be used for login.'
        );
        return;
      }
      await dispatch(getUserProfile());
      console.log(from);
      navigate(from, { replace: true });
    } catch (error) {
      console.log(error);
      toast.error('The username or current password is incorrect.');
    } finally {
      stopLoading();
    }
  };

  return (
    <AuthContainer>
      <OnboardingHeaderWrapper
        title="Please enter your email address"
        index={0}
        link="Create Account"
      />
      <Formik
        onSubmit={handleSubmit}
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={signinValidationSchema}
        enableReinitialize
      >
        {({
          values,
          touched,
          errors,
          isValid,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <TextInput
              label="Email"
              variant="normal"
              type="text"
              className="mb-3"
              placeHolder="Enter Email"
              htmlLabelFor="email"
              validState={
                touched.email && errors.email
                  ? 'inValid'
                  : touched.email
                  ? 'valid'
                  : undefined
              }
              value={values.email}
              helperMessage={errors.email}
              onChange={handleChange}
              onBlur={handleBlur}
              isClear={false}
            />
            <TextInput
              label="Password"
              variant="normal"
              type="password"
              className="mb-4 peer placeholder:text-lg"
              placeHolder="Enter Password"
              htmlLabelFor="password"
              validState={
                touched.password && errors.password
                  ? 'inValid'
                  : touched.password
                  ? 'valid'
                  : undefined
              }
              helperMessage={errors.password}
              value={values.password}
              onChange={handleChange}
              onClear={() => setFieldValue('password', '')}
              onBlur={handleBlur}
            />
            <p className="text-slate-500 text-[13px] font-medium font-SFProDisplay leading-[18px] mt-8">
              We’ll email you a magic code for a password-free sign in.
            </p>
            <p className="text-slate-500 text-[13px] font-medium font-SFProDisplay leading-[18px] mt-2">
              Or you can{' '}
              <span className="text-lightBlue hover:text-darkBlue font-bold">
                <Link to="">Sign In Manually</Link>
              </span>{' '}
              instead.
            </p>
            <Button
              variant="normal"
              size="sm"
              label="Continue"
              className="block w-full mx-0 mt-8 md:mt-14"
              type="submit"
              disabled={
                !isValid ||
                Object.keys(errors).length > 0 ||
                values?.email?.trim() === ''
              }
              isLoading={isLoading}
            />
          </form>
        )}
      </Formik>
    </AuthContainer>
  );
};
export default UserLogin;
